import { parse, parseISO } from 'date-fns';

/**
 * Parse a date value as **local time**, avoiding the UTC-midnight trap.
 *
 * `new Date('2026-05')` and `new Date('2026-05-01')` parse as UTC midnight,
 * which shifts to the **previous day/month** in timezones west of UTC.
 * This function detects date-only ISO strings and parses them with `date-fns
 * parse()` so the result is always in local time.
 *
 * Accepts strings, Date objects, epoch milliseconds, or nullish values.
 * - `string` — parsed safely (date-only patterns → local time, full ISO → parseISO)
 * - `Date` — returned as-is
 * - `number` — treated as epoch milliseconds
 * - `null | undefined` — returns current date (`new Date()`)
 *
 * @example
 * // In Houston (UTC-5):
 * new Date('2026-05')          → 2026-04-30T19:00 local  ✗
 * safeParseDate('2026-05')     → 2026-05-01T00:00 local  ✓
 *
 * // Other input types:
 * safeParseDate(existingDate)  → existingDate (pass-through)
 * safeParseDate(1716163200000) → new Date(1716163200000)
 * safeParseDate(null)          → new Date()
 */
export function safeParseDate(value: string | Date | number | null | undefined): Date {
  if (value == null) return new Date();
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (/^\d{4}-\d{2}$/.test(value)) return parse(value, 'yyyy-MM', new Date());
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return parse(value, 'yyyy-MM-dd', new Date());
  return parseISO(value);
}
